import { Subscription, Observable } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UploadHttpService } from '@core/services/upload-http.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { AppPermissionService } from '@core/services/app-permission.service';
import { PaymentModeDataService } from '../services/payment-mode-data.service';

@Component({
  selector: 'kt-payment-mode-edit',
  templateUrl: './payment-mode-edit.component.html',
  styleUrls: ['./payment-mode-edit.component.scss']
})
export class PaymentModeDialogComponent implements OnInit, OnDestroy {

  form: FormGroup;
  dropdown = {
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    statuses: this.dropdownHttpService.statuses,
    bankTypes: this.dropdownHttpService.merchantBankTypes,
    locales: [],
    types: [
      { id: 0, name: 'All' },
      { id: 1, name: 'Member' },
      { id: 2, name: 'Affiliate' },
    ],
    paymentProcessors: []
  };

  fieldTypes = [
    { id: 1, code: 'input_field', name: 'Input Field' },
    { id: 2, code: 'dropdown', name: 'Dropdown' }
  ];

  desktopLoading = false;
  mobileLoading = false;
  desktopImagePreview = [];
  mobileImagePreview = [];
  messages$ = this.paymentModeDataService.messages$;
  refreshStatus: boolean;
  buttonLoading = false;

  currencyDropdownSettings = {
    text: 'Please Select',
    enableSearchFilter: true,
    singleSelection: false,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'name',
    noDataLabel: 'No Data Found',
    showCheckbox: false,
  };

  processorDropdownSettings = {
    text: 'Please Select',
    enableSearchFilter: true,
    enableFilterSelectAll: false,
    singleSelection: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'name',
    noDataLabel: 'No Data Found',
    showCheckbox: false,
    maxHeight: 150,
    autoPosition: true,
  };

  defaultPosition = 99;

  // permissions
  canCreatePaymentMode: boolean;
  canUpdatePaymentMode: boolean;

  private subscription = new Subscription();
  private subscriptions = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { mode: string, paymentMode?: any },
    private dropdownHttpService: DropdownHttpService,
    private uploadService: UploadHttpService,
    private paymentModeDataService: PaymentModeDataService,
    public dialogRef: MatDialogRef<PaymentModeDialogComponent>,
    private appPermissionService: AppPermissionService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.formInit();
    if (this.dropdown.currencies.length === 0) {
      this.dropdownHttpService.currencies.subscribe(res => {
        this.dropdown.currencies = res;
      });
    }

    this.dropdownHttpService.locales.subscribe(res => {
      this.dropdown.locales = res;
      this.formInit();
    });

    this.dropdownHttpService.paymentProcessor.subscribe(res => {
      this.dropdown.paymentProcessors = res;
      this.refreshProcessorLabels();
    });

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreatePaymentMode = appPermissions.create_payment_mode;
      this.canUpdatePaymentMode = appPermissions.update_payment_mode;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
    this.onRefresh();
  }

  onCloseDialog(event?: Event) {
    this.dialogRef.close(this.refreshStatus);
  }

  onRefresh() {
    if (this.refreshStatus === true) {
      this.dialogRef.close(true);
    }
  }

  onSave() {
    this.buttonLoading = true;
    const formValue = this.form.value;

    const locales = [];
    if (formValue.locales) {
      Object.keys(formValue.locales).forEach(key => {
        const locale = formValue.locales[key];
        const positionNum = locale.position === null || locale.position === undefined || locale.position === '' ? this.defaultPosition : Number(locale.position);
        locales.push({
          ...locale,
          settings_locale_id: Number(locale.settings_locale_id),
          display_name: locale.display_name != null ? String(locale.display_name) : '',
          position: isNaN(positionNum) ? this.defaultPosition : positionNum
        });
      });
    }

    const payment_processors = [];
    if (formValue.payment_processors) {
      formValue.payment_processors.forEach((p: any) => {
        const selectedId = Array.isArray(p.payment_processor_id) && p.payment_processor_id.length > 0
          ? Number(p.payment_processor_id[0].id)
          : (p.payment_processor_id ? Number(p.payment_processor_id) : null);
        if (selectedId) {
          payment_processors.push({
            payment_processor_id: selectedId,
            weightage: Number(p.weightage)
          });
        }
      });
    }

    const data = {
      ...formValue,
      id: this.data.mode === 'edit' ? this.data.paymentMode.id : null,
      settings_currency_ids: formValue.settings_currency_ids ? formValue.settings_currency_ids.map(c => Number(c.id)) : [],
      deposit_field_type: Number(formValue.deposit_field_type),
      member_type: Number(formValue.member_type),
      status: Number(formValue.status),
      locales,
      payment_processors
    };

    delete data.settings_currency_id;

    Object.keys(data).forEach((key) => (data[key] == null || (data[key] === '')) && delete data[key]);

    const request: Observable<any> = (this.data.mode === 'create') ? this.paymentModeDataService.add(data) : this.paymentModeDataService.updatePaymentMode(data);

    this.subscription = request.pipe(
      tap((res: any) => {
        this.form.setErrors(null);
        this.buttonLoading = false;
        this.refreshStatus = true;
      }),
      catchError((error) => {
        this.buttonLoading = false;
        this.form.setErrors(null);
        throw error;
      })
    ).subscribe();
  }

  onUploadFile(event: any, type: string) {
    if (type === 'desktop') {
      this.desktopLoading = true;
    } else {
      this.mobileLoading = true;
    }
    const file: File = event.target.files[0];
    const formData = new FormData();
    if (file !== undefined) {
      formData.append('files', file, file.name);
      formData.append('type', 'bank');
      this.uploadService.upload(formData).subscribe(res => {
        if (type === 'desktop') {
          this.desktopLoading = false;
          this.desktopImagePreview = res;
          this.form.patchValue({
            desktop_image: this.desktopImagePreview[0]
          });
        } else {
          this.mobileLoading = false;
          this.mobileImagePreview = res;
          this.form.patchValue({
            mobile_image: this.mobileImagePreview[0]
          });
        }
        this.cdr.detectChanges();
      });
    } else {
      this.mobileLoading = false;
      this.desktopLoading = false;
    }
  }

  private buildLocalesForm() {
    let fields = {};
    this.dropdown.locales.forEach((element: any) => {
      const subFields = new FormGroup({
        settings_locale_id: new FormControl(element.id),
        display_name: new FormControl(null),
        position: new FormControl(this.defaultPosition, [Validators.pattern('[0-9]+')])
      });
      fields = { ...fields, [element.id]: subFields };
    });
    return fields;
  }

  get payment_processors() {
    return this.form.get('payment_processors') as FormArray;
  }

  createProcessorGroup(processor?: any) {
    let initialProcessor: any[] = [];
    if (processor && processor.payment_processor_id) {
      const found: any = this.dropdown.paymentProcessors.find((p: any) => Number(p.id) === Number(processor.payment_processor_id));
      initialProcessor = [{
        id: Number(processor.payment_processor_id),
        name: found ? found.name : ''
      }];
    }
    return new FormGroup({
      payment_processor_id: new FormControl(initialProcessor, [Validators.required, Validators.minLength(1)]),
      weightage: new FormControl(processor ? processor.weightage : 1, [Validators.required, Validators.min(1), Validators.max(100), Validators.pattern(/^[0-9]+$/)])
    });
  }

  addProcessor() {
    this.payment_processors.push(this.createProcessorGroup());
    this.cdr.detectChanges();
  }

  removeProcessor(index: number) {
    this.payment_processors.removeAt(index);
    this.cdr.detectChanges();
  }

  onCheckWeightage(event: any, index: number) {
    const value = event.target.value;
    if (value === '') return;

    const numValue = Number(value);
    if (numValue > 100) {
      this.payment_processors.at(index).get('weightage').setValue(100);
    } else if (numValue < 1) {
      this.payment_processors.at(index).get('weightage').setValue(1);
    }
  }

  getAvailableProcessors(index: number) {
    const selectedIds = this.payment_processors.value
      .map((p: any, i: number) => {
        if (i === index) return null;
        const v = p.payment_processor_id;
        if (Array.isArray(v) && v.length > 0) return Number(v[0].id);
        return v ? Number(v) : null;
      })
      .filter((id: number | null) => id !== null);

    return this.dropdown.paymentProcessors.filter((p: any) => !selectedIds.includes(Number(p.id)));
  }

  getSelectedProcessor(index: number): any {
    const control = this.payment_processors.at(index).get('payment_processor_id');
    const value = control ? control.value : null;
    let id: number | null = null;
    if (Array.isArray(value) && value.length > 0) {
      id = Number(value[0].id);
    } else if (value !== null && value !== undefined && value !== '') {
      id = Number(value);
    }
    if (id === null || isNaN(id)) return null;
    const processor: any = this.dropdown.paymentProcessors.find((p: any) => Number(p.id) === id);
    if (!processor) return null;
    return {
      ...processor,
      min_deposit: processor.min_deposit !== undefined ? processor.min_deposit : null,
      max_deposit: processor.max_deposit !== undefined ? processor.max_deposit : null,
    };
  }

  private refreshProcessorLabels() {
    if (!this.payment_processors) return;
    this.payment_processors.controls.forEach((control: any) => {
      const ctl = control.get('payment_processor_id');
      const value = ctl ? ctl.value : null;
      if (Array.isArray(value) && value.length > 0 && value[0].id) {
        const processor: any = this.dropdown.paymentProcessors.find((p: any) => Number(p.id) === Number(value[0].id));
        if (processor && value[0].name !== processor.name) {
          ctl.setValue([{ id: Number(processor.id), name: processor.name }], { emitEvent: false });
        }
      }
    });
  }

  onNumberOnly(event: KeyboardEvent) {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  onAmountShortcut(event: KeyboardEvent) {
    const charCode = (event.which) ? event.which : event.keyCode;
    // Allow numbers (48-57) and comma (44)
    if (charCode !== 44 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  private formInit() {
    let name = null;
    let settings_currency_ids = [];
    let member_type = 0;
    let status = 1;
    let desktop_image = null;
    let mobile_image = null;
    let deposit_field_type = 1;
    let shortcut_amount = null;

    if (this.data.mode === 'edit' && this.data.paymentMode) {
      name = this.data.paymentMode.name;
      settings_currency_ids = this.data.paymentMode.payment_mode_currency.map(pm => {
        const currency = this.dropdown.currencies
          .find(c => c.id === pm.settings_currency_id);

        return currency ? { id: currency.id, name: currency.name } : null;
      })
        .filter(Boolean);
      member_type = this.data.paymentMode.member_type;
      status = this.data.paymentMode.status;
      desktop_image = this.data.paymentMode.desktop_image;
      mobile_image = this.data.paymentMode.mobile_image;
      deposit_field_type = this.data.paymentMode.deposit_field_type;
      shortcut_amount = this.data.paymentMode.shortcut_amount;
    }

    this.form = new FormGroup({
      name: new FormControl(name, [Validators.required]),
      settings_currency_ids: new FormControl(settings_currency_ids, [Validators.required]),
      deposit_field_type: new FormControl(deposit_field_type, [Validators.required]),
      shortcut_amount: new FormControl(shortcut_amount, [Validators.pattern(/^[0-9,]*$/)]),
      member_type: new FormControl(member_type, [Validators.required]),
      status: new FormControl(status, [Validators.required]),
      desktop_image: new FormControl(desktop_image),
      mobile_image: new FormControl(mobile_image),
      locales: new FormGroup(this.buildLocalesForm()),
      payment_processors: new FormArray([])
    });

    if (this.data.mode === 'edit' && this.data.paymentMode && this.data.paymentMode.payment_mode_payment_processor) {
      this.data.paymentMode.payment_mode_payment_processor.forEach((processor: any) => {
        this.payment_processors.push(this.createProcessorGroup(processor));
      });
    }

    if (this.data.mode === 'create' && this.payment_processors.length === 0) {
      this.addProcessor();
    }

    const locales = this.data.paymentMode?.locales || this.data.paymentMode?.payment_mode_locales;
    if (this.data.mode === 'edit' && this.data.paymentMode && locales) {
      locales.forEach((element: any) => {
        if (this.form.get(`locales.${element.settings_locale_id}`)) {
          this.form.get(`locales.${element.settings_locale_id}`).patchValue({
            display_name: element.display_name,
            position: element.position
          });
        }
      });
    }
  }
}
